import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { KYBSTATUS, ROLES } from "./utils/constants";

// Auth pages
import Login from "./pages/Auth/Login";
import SetPassword from "./pages/Auth/SetPassword";
import ForgotPassword from "./pages/Auth/ForgotPassword";

// Onboarding / KYB
import KYBForm from "./pages/Onboarding/KYB/index";
import KYBPending from "./pages/Onboarding/KYB/Pending";
import KYBRejected from "./pages/Onboarding/KYB/Rejected";

// Receiver pages
import ReceiverOverview from "./pages/Receiver/Invoice/Overview";
import ReceiverAddBank from "./pages/Receiver/AddBank";
import ReceiverTransactions from "./pages/Receiver/Transactions";
import ReceiverSenders from "./pages/Receiver/Sender/Senders";
import ReceiverCreateSender from "./pages/Receiver/Sender/CreateSender";
import ReceiverSenderDetail from "./pages/Receiver/Sender/SenderDetail";
import ReceiverTreasury from "./pages/Receiver/Treasury";
import ReceiverCreateInvoice from "./pages/Receiver/Invoice/CreateInvoice";
import ReceiverInvoiceDetail from "./pages/Receiver/Invoice/InvoiceDetail";
import ReceiverTransactionDetail from "./pages/Receiver/TransactionDetail";

// Sender pages
import SenderInvoices from "./pages/Sender/Invoice/Invoices";
import SenderPayInvoice from "./pages/Sender/Invoice/PayInvoice";
import SenderTransactions from "./pages/Sender/Transactions";
import SenderCreateTransaction from "./pages/Sender/CreateTransaction";
import SenderTreasury from "./pages/Sender/Treasury";
import SenderFundWallet from "./pages/Sender/FundWallet";
import SenderTransactionDetail from "./pages/Sender/TransactionDetail";

// Account pages (role-agnostic)
import ChangePassword from "./pages/Account/ChangePassword";

// Forbidden page (404/403 with auto-logout countdown)
import Forbidden from "./pages/Forbidden";
import OtcList from "./pages/Sender/OTC/OtcList";
import OtcDetail from "./pages/Sender/OTC/OtcDetail";

// Role-aware auth guard (general)
const RequireAuth = ({ children }) => {
  const token = useSelector((s) => s.auth.accessToken);
  return token ? children : <Navigate to="/auth/login" replace />;
};

// Role-restricted auth guard (enforces sender vs receiver access)
const RequireRole = ({ role: allowedRole, children }) => {
  const token = useSelector((s) => s.auth.accessToken);
  const userRole = useSelector((s) => s.auth.role);
  const userStatus = useSelector((s) => s.auth.status);

  if (!token) return <Navigate to="/auth/login" replace />;

  if (allowedRole && userRole !== allowedRole) {
    return <Navigate to="/forbidden" replace />;
  }

  // If the KYBSTATUS is Pending/Rejected Then don't allow them to access any other pages
  if (userStatus === KYBSTATUS.PENDING) return <Navigate to="/onboarding/kyb" replace />;

  if (userStatus === KYBSTATUS.REJECTED) return <Navigate to="/onboarding/kyb/rejected" replace />;

  return children ? children : <Outlet />;
};

// Public-only guard (prevents logged-in users from viewing auth screens)
const PublicOnly = ({ children }) => {
  const token = useSelector((s) => s.auth.accessToken);
  const role = useSelector((s) => s.auth.role);

  if (token) {
    if (role === ROLES.RECEIVER) return <Navigate to="/receiver/overview" replace />;
    if (role === ROLES.SENDER) return <Navigate to="/sender/invoices" replace />;
  }

  return children;
};

// Role redirect guard
const RoleRedirect = () => {
  const role = useSelector((s) => s.auth.role);
  if (role === ROLES.RECEIVER) return <Navigate to="/receiver/overview" replace />;
  if (role === ROLES.SENDER) return <Navigate to="/sender/invoices" replace />;
  return <Navigate to="/auth/login" replace />;
};

// Status-aware onboarding guard (pending / rejected)
const RequireStatus = ({ requiredStatus, children }) => {
  const token = useSelector((s) => s.auth.accessToken);
  const status = useSelector((s) => s.auth.status);

  if (!token) return <Navigate to="/auth/login" replace />;

  if (status === requiredStatus) {
    return children;
  }

  return <RoleRedirect />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RoleRedirect />} />

      {/* Auth */}
      <Route path="/auth/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/auth/set-password" element={<SetPassword />} />
      <Route path="/auth/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />

      {/* Onboarding — KYB */}
      {/* /onboarding/kyb is only accessible when REACT_APP_KYB_ENABLED=true */}
      <Route
        path="/onboarding/kyb"
        element={
          <RequireAuth>
            {process.env.REACT_APP_KYB_ENABLED === "true"
              ? <KYBForm />
              : <Navigate to="/onboarding/kyb/pending" replace />}
          </RequireAuth>
        }
      />
      <Route path="/onboarding/kyb/pending" element={<RequireStatus requiredStatus={KYBSTATUS.PENDING}><KYBPending /></RequireStatus>} />
      <Route path="/onboarding/kyb/rejected" element={<RequireStatus requiredStatus={KYBSTATUS.REJECTED}><KYBRejected /></RequireStatus>} />

      {/* ── Receiver ──────────────────────────────────────────────────────────── */}
      <Route element={<RequireRole role={ROLES.RECEIVER} />}>
        <Route path="/receiver/overview" element={<ReceiverOverview />} />
        <Route path="/receiver/overview/add-bank" element={<ReceiverAddBank />} />
        <Route path="/receiver/invoices/create" element={<ReceiverCreateInvoice />} />
        <Route path="/receiver/invoices/:id" element={<ReceiverInvoiceDetail />} />
        <Route path="/receiver/transactions" element={<ReceiverTransactions />} />
        <Route path="/receiver/transactions/:id" element={<ReceiverTransactionDetail />} />
        <Route path="/receiver/senders" element={<ReceiverSenders />} />
        <Route path="/receiver/senders/create" element={<ReceiverCreateSender />} />
        <Route path="/receiver/senders/:id" element={<ReceiverSenderDetail />} />
        <Route path="/receiver/treasury" element={<ReceiverTreasury />} />
      </Route>

      {/* ── Sender ────────────────────────────────────────────────────────────── */}
      <Route element={<RequireRole role={ROLES.SENDER} />}>
        <Route path="/sender/invoices" element={<SenderInvoices />} />
        <Route path="/sender/invoices/:id/pay" element={<SenderPayInvoice />} />
        <Route path="/sender/transactions" element={<SenderTransactions />} />
        <Route path="/sender/transactions/new" element={<SenderCreateTransaction />} />
        <Route path="/sender/transactions/:id" element={<SenderTransactionDetail />} />
        <Route path="/sender/treasury" element={<SenderTreasury />} />
        <Route path="/sender/treasury/fund" element={<SenderFundWallet />} />
        {/* OTC Requests */}
        <Route path="/sender/otc-requests" element={<OtcList />} />
        <Route path="/sender/otc-requests/:otcRequestId" element={<OtcDetail />} />

      </Route>

      {/* ── Account (role-agnostic) ────────────────────────────────────────── */}
      <Route path="/account/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />

      {/* Legacy aliases */}
      <Route path="/login" element={<Navigate to="/auth/login" replace />} />

      {/* Catch-all / Forbidden / 404 routes */}
      <Route path="*" element={<RequireAuth><Forbidden /></RequireAuth>} />
    </Routes>
  );
};

export default AppRoutes;

